import React, { useState, useEffect, useRef } from "react";
import "./navbar.css";
import {
  nitrologo,
  menuopen,
  menuclose,
} from "../../utilities/images";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useCart } from "../chooseride/CartContext";
import AuthButtons from "./AuthButtons";
import RideProfileDropdown from "../chooseride/RideProfileDropdown";
import SubNavbar from "./SubNavbar";

function Navbar({ showSubNavbar = true }) {
  // It's generally better to have the mobile menu closed by default.
  const [openMenu, setOpenMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { clearAllCartData } = useCart();
  const profileDropdownRef = useRef(null);

  // Function to close the mobile menu when a link is clicked
  const closeMenu = () => {
    setOpenMenu(false);
  };

  // Handle scroll effect for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle clicks outside profile dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle user logout
  const handleLogout = async () => {
    try {
      setIsProfileDropdownOpen(false);
      await auth.signOut();
      if (clearAllCartData) {
        clearAllCartData();
      }
      // Clean up storage on logout
      localStorage.removeItem('9troride_address');
      localStorage.removeItem('9troride_locationType');
      sessionStorage.removeItem('9troride_checkoutVendorId');
      sessionStorage.removeItem('9troride_checkoutVendorType');
      navigate('/');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const handleLinkClick = () => {
    closeMenu();
    setIsProfileDropdownOpen(false);
  };

  const avatarName =
    auth.riderData?.first_name ||
    auth.currentUser?.displayName?.split(' ')[0] ||
    'Account';

  return (
    <>
      <div className={`navbar ${scrolled ? "scrolled" : ""} ${openMenu ? "mobile-open" : ""}`}>
        <div className='navbar-container'>
          <div className='navbar-logo'>
            <img src={nitrologo} alt='9troride' onClick={() => navigate("/")} />
          </div>
          <div className={`navbar-links ${openMenu ? "active" : ""}`}>
            <div className="navbar-main-links">
              <Link to='/' onClick={handleLinkClick}>
                Ride
              </Link>
              <Link to='/9troeat' onClick={handleLinkClick}>
                Food
              </Link>
              <Link to='/pricing' onClick={handleLinkClick}>
                Pricing
              </Link>
              <Link to='/about' onClick={handleLinkClick}>
                About us
              </Link>
            </div>
            
            <div className="navbar-mobile-footer">
              {auth.currentUser ? (
                <div className="navbar-mobile-auth">
                  <button className="navbar-profile-btn mobile-profile-btn" onClick={() => { navigate('/settings'); closeMenu(); }}>
                    <img 
                      src={auth.currentUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}&background=f0f0f0&color=111`} 
                      alt="Profile" 
                      className="navbar-profile-avatar"
                    />
                    <span>{avatarName}</span>
                  </button>
                  <button className="navbar-mobile-logout-btn" onClick={handleLogout}>
                    Log Out
                  </button>
                </div>
              ) : (
                <AuthButtons variant="mobile" onLinkClick={handleLinkClick} state={{ from: location }} />
              )}
            </div>
          </div>
          <div className="navbar-right">
            {auth.currentUser ? (
              <div className="navbar-profile-section" ref={profileDropdownRef}>
                <button className="navbar-profile-btn" onClick={() => setIsProfileDropdownOpen(prev => !prev)}>
                  <img 
                    src={auth.currentUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}&background=f0f0f0&color=111`} 
                    alt="Profile" 
                    className="navbar-profile-avatar"
                  />
                  <span>{avatarName}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`chevron-icon ${isProfileDropdownOpen ? 'open' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
                </button>
                {isProfileDropdownOpen && (
                  <RideProfileDropdown user={auth.currentUser} rider={auth.riderData} onLogout={handleLogout} />
                )}
              </div>
            ) : (
              <AuthButtons onLinkClick={handleLinkClick} state={{ from: location }} />
            )}
            <div className='navbar-mobile'>
              <button onClick={() => setOpenMenu(!openMenu)}>
                <img
                  src={openMenu ? menuclose : menuopen}
                  alt={openMenu ? "Close menu" : "Open menu"}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      {showSubNavbar && <SubNavbar marginTop="72px" />}
      {openMenu && <div className="navbar-overlay" onClick={closeMenu} />}
    </>
  );
}

export default Navbar;
